import type { Messages } from './en';

/**
 * Fase E (paquet Xinyi): el districte del Taipei 101, Xinyi, la Muntanya de l’Elefant i la zona de l’Estació Central.
 * Les línies de pràctica d’anglès es queden en anglès expressament.
 */
export const caXinyi: Pick<Messages, 'xinyi'> = {
  xinyi: {
    mapNames: {} as Record<string, string>,
    zones: {
      xy_xiangshan: 'Muntanya de l’Elefant',
      xy_viewpoint: 'Mirador de la Muntanya de l’Elefant',
      xy_sysHall: 'Sala Commemorativa de Sun Yat-sen',
      xy_avenue: 'Avinguda Zhongxiao Est',
      xy_arcades: 'Porxos amb botigues',
      xy_stationFront: 'Davant de l’estació',
      xy_village: 'Poblet Quatre Quatre Sud',
      xy_xinyi: 'Zona comercial de Xinyi',
    },
    objects: {
      xy_taxi: 'Parada de taxis',
      xy_sysHall: 'Sala Commemorativa de Sun Yat-sen',
      xy_cuihu: 'Estany Cuihu',
      xy_mrtSys: 'MRT · Sala Commemorativa de Sun Yat-sen',
      xy_mall101: 'Centre comercial Taipei 101',
      xy_love: 'L’escultura «愛»',
      xy_fireworks: 'Cartell dels focs de Cap d’Any',
      xy_conv: 'Botiga Bon Veí',
      xy_tea: 'Teteria Bubble Bear',
      xy_pharmacy: 'Farmàcia',
      xy_trash: 'Parada del camió de les escombraries',
      xy_shrine: 'Altar del Déu de la Terra',
      xy_village: 'Poblet Quatre Quatre Sud',
      xy_bagel: 'Cafè Good Slope',
      xy_market1: 'Parada del mercat: sabons i cistells',
      xy_market2: 'Parada del mercat: plantetes',
      xy_market3: 'Parada del mercat: postals',
      xy_dept: 'Grans magatzems Galaxy',
      xy_books: 'Llibreria Book Tree',
      xy_screen: 'Pantalla gegant de la Plaça Moonlight',
      xy_stage: 'Racó d’artistes de carrer',
      xy_mrt101: 'MRT · Taipei 101 / World Trade Center',
      xy_trailhead: 'Inici del sender de la Muntanya de l’Elefant',
      xy_rocks: 'Les Sis Roques Gegants',
      xy_viewpoint: 'Mirador de la Muntanya de l’Elefant',
      xy_easycard: 'Màquina per recarregar l’EasyCard',
      xy_priority: 'Seients reservats',
    },
    npc: {
      xy_office1: 'Oficinista',
      xy_office2: 'Oficinista',
      xy_tourDad: 'Pare turista',
      xy_tourKid: 'Nena turista',
      xy_grandma: 'L’àvia Lin',
      xy_hiker: 'Excursionista',
      xy_clerk: 'Llibretera',
      xy_teaSeller: 'Venedor de te de bombolles',
      xy_trashLady: 'Veïna',
      xy_vendor: 'Artesana del mercat',
      xy_taichi: 'L’avi del taitxí',
      xy_taxiDriver: 'Taxista',
      xy_performer: 'Artista de globus',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    common: {
      quiz: '📝 Test d’anglès',
      right: '✅ Molt bé!',
      wrong: '❌ Gairebé. Torna-ho a provar!',
      back: 'Potser després',
    },
    welcome: 'Benvingut a Taipei! El Taipei 101 és a l’est, passada la Sala Commemorativa. Al darrere hi ha la Muntanya de l’Elefant.',

    sysHall: {
      text: 'La Sala Commemorativa de Sun Yat-sen honora el doctor Sun Yat-sen, el pare fundador de la República de la Xina. Mira la teulada groga daurada: les cantonades es corben cap amunt com les ales d’un ocell. A dins, la guàrdia d’honor s’està immòbil al costat d’una estàtua de bronze enorme, i fa el relleu cada hora.',
      watch: '💂 Mirar la guàrdia',
      watched: 'Clac, clac! Els guàrdies desfilen perfectament sincronitzats i després es queden quiets com estàtues.',
      q: 'Pràctica d’anglès: “What colour is the roof of the Memorial Hall?”', // English practice
      a: ['It is yellow.', 'It is green.', 'It is black.'], // English practice
      ok: '✅ Sí! Teules vidriades grogues que brillen al sol.',
    },
    cuihu: {
      text: 'Cuihu vol dir «llac verd». Carpes koi vermelles, blanques i daurades neden entre les fulles de lotus, i les tortugues prenen el sol a les roques.',
      look: '🐟 Mirar els peixos',
      say: 'Look! The fish are so colourful!', // English practice
      looked: 'Una koi taronja enorme obre la boca davant teu. Blub!',
    },
    mall: {
      text: 'El centre comercial Taipei 101 ocupa les cinc primeres plantes de la torre: moda, un supermercat i una gran zona de restaurants.',
      q: 'Pràctica d’anglès: preguntes “Excuse me, where is the food court?” i el vigilant respon. Quina resposta té sentit?', // English practice
      a: ['It is on the basement level, B1. Take the escalator down.', 'It is in my pocket.', 'It is on the moon, next to the cat.'], // English practice
      ok: '✅ Correcte! A Taipei les zones de restaurants solen ser al soterrani.',
      browse: '👀 Mirar aparadors',
      browsed: 'Botigues lluents i aire condicionat fresquet. Només mires… i és divertit!',
    },
    love: {
      text: 'A la plaça hi ha una escultura vermella gegant del caràcter 愛 («amor»). Tothom s’hi fa una foto!',
      ask: '📸 Demanar a algú que et faci una foto',
      howAsk: 'Com es demana amb educació?',
      ok: 'Excuse me, could you take a photo of me, please?', // English practice
      bad: ['Photo. Now. You.', 'Give me your camera.'], // English practice
      done: '“Sure! Say cheese!” Clic! Una foto preciosa amb un gran 愛 vermell.',
    },
    fireworks: {
      text: 'Cada Cap d’Any, a mitjanit, surten focs artificials de totes les plantes del Taipei 101 durant uns cinc minuts. Centenars de milers de persones fan el compte enrere juntes a Xinyi.',
      q: 'Pràctica d’anglès: “When do the fireworks start?”', // English practice
      a: ['At midnight, when the new year begins.', 'At lunchtime on Tuesdays.', 'Every morning at seven.'], // English practice
      ok: '✅ Correcte! Deu, nou, vuit… Bon any nou!',
      countdown: 'Ten, nine, eight, seven, six, five, four, three, two, one… Happy New Year!', // English practice
    },
    conv: {
      text: 'Ding-dong! El timbre de la porta de la botiga Bon Veí. A Taiwan, les botigues de conveniència venen berenars, serveixen per pagar rebuts i imprimir documents, i obren les 24 hores.',
      snack: '🥚 Comprar un ou de te i aigua',
      bag: '🛍️ «Necessites bossa?»',
      food: { vendor: 'Welcome! Anything else?', ok: 'One tea egg and a bottle of water, please.', bad: ['Egg. Water. Go.', 'I want the whole shop for free.'], done: 'Un ou de te calentó que fa olor de salsa de soja i anís estrellat, i aigua fresca. Perfecte!', price: 25 }, // English practice
      bagQ: 'Pràctica d’anglès: el dependent pregunta “Do you need a bag?”. Tu ja portes la teva bossa. Què dius?', // English practice
      bagA: ['No, thanks. I have one.', 'Bag is me.', 'Yes, I need ten bags for one egg.'], // English practice
      bagOk: '✅ Genial! A Taiwan les bosses de plàstic es paguen, així que la gent porta la seva.',
    },
    tea: {
      text: 'Bubble Bear 泡泡熊: el te amb llet i perles es va inventar a Taiwan! Tu tries el sucre (全糖 tot, 半糖 la meitat, 微糖 una mica, 無糖 gens) i el gel (少冰 poc gel, 去冰 sense gel).',
      menu: 'Què et ve de gust?',
      milk: '🧋 Te amb llet i perles',
      green: '🍵 Te verd de gessamí',
      milkOrder: { vendor: 'One pearl milk tea! How much sugar and ice?', ok: 'Half sugar, less ice, please.', bad: ['Sugar sugar ice ice!', 'Give me all the sugar and no cup.'], done: 'Remena, remena i pop! Entra la palleta. Perles toves i te cremós, i no gaire dolç!', price: 50 }, // English practice
      greenOrder: { vendor: 'Jasmine green tea! Sugar and ice level?', ok: 'No sugar and no ice, please.', bad: ['Tea. Cold. Sugar? Maybe.', 'I want a hot ice tea.'], done: 'Suau, amb aroma de flors i refrescant. Un dels preferits de la gent d’aquí!', price: 35 }, // English practice
    },
    pharmacy: {
      text: '藥局 vol dir farmàcia. El farmacèutic et pot ajudar amb problemes petits, com un refredat o un mal de cap.',
      q: 'Pràctica d’anglès: et fa mal el cap. Què preguntes?', // English practice
      a: ['Excuse me, do you have something for a headache?', 'My head is a banana.', 'Give me all the medicine.'], // English practice
      ok: '✅ Perfecte. El farmacèutic somriu: “Drink water and get some rest!”',
    },
    trash: {
      text: 'A Taipei gairebé no hi ha papereres al carrer. Cada vespre arriba el camió groc de les escombraries tocant música —avui gairebé sempre «La pregària d’una verge», de Bądarzewska (en alguns llocs encara sona «Per a Elisa», de Beethoven)— i tothom surt corrents amb les bosses. El reciclatge va en un segon camió.',
      here: '🎵 Ja és aquí el camió de les escombraries! Els veïns surten de pressa amb les bosses.',
      notYet: 'El camió passa per aquí cap a les 8 del vespre.',
      q: 'Pràctica d’anglès: què dius quan surts amb les bosses?', // English practice
      a: ['I am taking out the trash!', 'I am taking out the dog’s homework!', 'I am eating the trash!'], // English practice
      ok: '✅ Sí! I no t’oblidis de separar el reciclatge.',
      toast: '🎵 Ta-ra-ra-ra… «La pregària d’una verge»! El camió de les escombraries baixa pel carrer.',
    },
    shrine: {
      text: 'Un altar petitó per a Tudigong (土地公), el Déu de la Terra que protegeix el barri. La gent fa reverències amb encens i li deixa fruita, te o galetes.',
      bow: '🙏 Fer tres reverències',
      wish: 'Please keep my family safe and healthy.', // English practice
      bowed: 'Fas tres reverències. El fum de l’encens puja recargolant-se cap al banià.',
    },
    village: {
      text: 'El Poblet Quatre Quatre Sud (四四南村) es va construir el 1948 per a les famílies dels treballadors de l’arsenal número 44. Les casetes baixes de maó gris ara són un petit museu, botigues i un cafè, just a sota del Taipei 101: el vell i el nou, l’un al costat de l’altre.',
      q: 'Pràctica d’anglès: “Who lived in these houses in the old days?”', // English practice
      a: ['Military families.', 'Pirates.', 'Astronauts.'], // English practice
      ok: '✅ Correcte! Hi havia poblets de famílies de militars (眷村) per tot Taiwan.',
      market: 'Cada diumenge la gespa es converteix en el Simple Market: artesania, plantes i berenars.',
      noMarket: 'El Simple Market només es fa els diumenges. Torna llavors!',
    },
    bagel: { vendor: 'Hi! Our bagels are fresh from the oven. What would you like?', ok: 'Could I have a sesame bagel and a cup of tea, please?', bad: ['Bagel! Hurry!', 'I want a bagel made of tea.'], done: 'Un bagel de sèsam calentó i esponjós, i un te, en una casa antiga del poblet. Que s’hi està bé!', price: 45 }, // English practice
    market: {
      ask: 'How much is this, please?', // English practice
      stalls: {
        xy_market1: { text: 'Sabó artesà que fa olor d’aranja i cistellets teixits.', answer: 'It is thirty dollars.', price: 30 }, // English practice
        xy_market2: { text: 'Suculentes diminutes en tasses de te i tests fets amb llaunes velles.', answer: 'It is twenty dollars.', price: 20 }, // English practice
        xy_market3: { text: 'Postals dibuixades a mà del Taipei 101, el MRT i els gats del carrer.', answer: 'It is fifteen dollars.', price: 15 }, // English practice
      },
      buy: (n: number) => `🛍️ Comprar-ho (${n} monedes)`,
      bought: 'Has comprat una cosa feta a mà. L’artesana te l’embolica amb paper de diari i un somriure.',
      poor: 'No tens prou monedes.',
    },
    dept: {
      text: 'Grans magatzems Galaxy: deu plantes de botigues, unides als edificis veïns per passarel·les elevades. A la tardor tots els magatzems fan unes rebaixes d’aniversari enormes (週年慶).',
      q: 'Pràctica d’anglès: t’agrada una samarreta. Què li preguntes a la dependenta?', // English practice
      a: ['Excuse me, can I try this on?', 'T-shirt! Me! Now!', 'How old is this shirt?'], // English practice
      ok: '✅ “Of course! The fitting room is over there.”',
    },
    books: {
      text: 'Book Tree 書林: una llibreria famosa on la gent s’asseu a terra i llegeix durant hores. Moltes plantes de llibres, música, papereria i una cafeteria.',
      read: '📖 Llegir una estona',
      readDone: 'Llegeixes un llibre il·lustrat en anglès sobre un gat que va en MRT. El temps vola!',
      askClerk: '🗣️ Preguntar pels llibres en anglès',
      clerkSays: 'La llibretera diu: “English books? They are on the second floor, next to the stairs.”', // English practice
      q: 'On són els llibres en anglès?',
      a: ['On the second floor, next to the stairs.', 'In the basement, next to the café.', 'On the roof.'], // English practice
      ok: '✅ Molt ben escoltat! Escales amunt.',
      closed: 'Book Tree ara és tancada. Obre a les 10 del matí.',
    },
    screen: {
      text: 'La pantalla gegant de la Plaça Moonlight fa anuncis, videoclips i curiositats de la ciutat.',
      q: 'Pràctica d’anglès: la pantalla pregunta “How tall is Taipei 101?”', // English practice
      a: ['It is 508 metres tall.', 'It is 50 metres tall.', 'It is 5,000 metres tall.'], // English practice
      ok: '✅ Correcte! Va ser l’edifici més alt del món del 2004 al 2010.',
    },
    stage: {
      text: 'Un racó per a artistes de carrer. A Taipei necessiten una llicència d’artista de carrer, i actuen aquí cada vespre.',
      watch: '🎶 Mirar l’espectacle',
      watched: 'Música, globus i aplaudiments. El públic ho celebra!',
      tip: '🪙 Donar 5 monedes de propina',
      thanks: 'That was amazing! Thank you!', // English practice
      empty: 'Ara no actua ningú. Torna a la tarda o al vespre.',
    },
    trail: {
      text: 'Sender de la Muntanya de l’Elefant (象山步道): uns 20 minuts d’esglaons de pedra empinats fins al mirador sobre el Taipei 101. Porta aigua i descansa quan ho necessitis!',
      q: 'Pràctica d’anglès: “How long does it take to get to the top?”', // English practice
      a: ['About twenty minutes.', 'About twenty days.', 'Twenty seconds.'], // English practice
      ok: '✅ Correcte! Som-hi: esglaó a esglaó.',
      tired: 'Uf, quants esglaons! (−energia)',
    },
    rocks: {
      text: 'Les Sis Roques Gegants (六巨石): sis roques enormes a prop del cim de la Muntanya de l’Elefant. Els excursionistes s’hi enfilen per fer-se una foto famosa.',
      climb: '🧗 Enfilar-se a una roca',
      careful: 'Be careful! Hold on tight.', // English practice
      climbed: 'T’hi enfiles amb compte i saludes des de dalt. La ciutat brilla als teus peus!',
    },
    viewpoint: {
      text: 'Aquesta és la vista clàssica: el Taipei 101 alçant-se per sobre de tota la ciutat.',
      shoot: '📸 Fer la foto',
      say: 'What a view! Taipei 101 looks so tall from here.', // English practice
      title: '📸 Muntanya de l’Elefant',
      caption: 'La teva foto del Taipei 101 des de la Muntanya de l’Elefant.',
      tap: 'Toca per tancar',
    },
    taxi: {
      text: 'A Taipei hi ha taxis grocs a tot arreu. El taxista pregunta: “Where to?”', // English practice
      to: {
        station: 'To Taipei Main Station, please.',
        tower: 'To Taipei 101, please.',
        village: 'To Four Four South Village, please.',
        trail: 'To the Elephant Mountain trailhead, please.',
      }, // English practice
      price: (n: number) => `${n} monedes`,
      poor: 'Necessites més monedes per al taxi.',
      ride: '🚕 Som-hi! “Please fasten your seat belt.”', // English practice
    },
    easycard: {
      text: 'La màquina de l’EasyCard: posa la targeta al lector, tria un import i fica-hi els diners. L’EasyCard serveix per al MRT, els autobusos, les YouBike i les botigues de conveniència.',
      top: '💳 Recarregar la meva targeta',
      q: 'Pràctica d’anglès: la pantalla diu “Please place your card on the reader.” Què fas primer?', // English practice
      a: ['I put my card on the reader.', 'I put my card in my mouth.', 'I shout at the machine.'], // English practice
      q2: 'Pràctica d’anglès: “How much would you like to add?”', // English practice
      a2: ['One hundred dollars, please.', 'My name is one hundred.', 'Yes, I would like a train.'], // English practice
      ok: '✅ Pip! Targeta recarregada. El saldo surt a la pantalla.',
    },
    priority: {
      text: 'Els seients blau fosc són seients reservats (博愛座). Qualsevol s’hi pot asseure, però els cedeixes a qui els necessiti més.',
      q: 'Pràctica d’anglès: puja al tren un senyor gran. Què li dius?', // English practice
      a: ['Please, take my seat.', 'This is my seat forever.', 'Go away, please.'], // English practice
      ok: '✅ Que amable! Ell et diu: “Thank you, young friend.”',
    },

    talk: {
      guideTip: 'Consell: els taxis grocs esperen al costat de l’estació. El Taipei 101 és a l’est, i la Muntanya de l’Elefant just al darrere!',
      office1: [
        'Hora de dinar! Al migdia la zona de restaurants del soterrani està plena a vessar.',
        'Treballo a la planta 35 d’una torre d’allà. El meu ascensor no és tan ràpid com el del 101!',
        'Pràctica d’anglès: “I have a meeting at two.” “See you later!”', // English practice
      ],
      office2: [
        'Vaig amb MRT cada dia. Passo l’EasyCard i llestos!',
        'En sortir de la feina m’agrada anar per les passarel·les elevades. No has de travessar cap carrer!',
        'Pràctica d’anglès: “Excuse me, is this seat taken?” “No, go ahead.”', // English practice
      ],
      tourAsk: 'Excuse me! We are lost. How do we get to Taipei 101?', // English practice
      tourQ: 'Com s’indica el camí?',
      tourA: ['Go straight along this road, then look for the tallest tower. You can’t miss it!', 'Taipei 101 is in my bag.', 'Turn left, turn left, turn left, turn left.'], // English practice
      tourOk: '“Thank you so much! You are very kind.” La família s’acomiada amb la mà i se’n va cap a l’est.',
      tourKid: ['M’encanten les motos! N’hi ha moltíssimes!', 'El meu pare diu que després prendrem te de bombolles!', 'Pràctica d’anglès: “What is your name?” “My name is Lily. Nice to meet you!”'], // English practice
      grandmaShrine: [
        'Vinc a veure en Tudigong cada matí. Ell protegeix el nostre carrer.',
        'Tres reverències, així. A poc a poc, a poc a poc.',
        'Pràctica d’anglès: “Good morning, Grandma!” Digues-m’ho a mi!', // English practice
      ],
      grandmaMarket: [
        'Ai, el mercat del diumenge! Sempre em compro una planteta.',
        'Jo em vaig criar en un poblet com aquest. Ho compartíem tot amb els veïns.',
      ],
      hiker: [
        'Endavant! La vista de dalt val cada esglaó.',
        'Pràctica d’anglès: “I am out of breath!” “Let’s take a short break.”', // English practice
        'A la posta de sol s’il·lumina el 101. Queda’t a veure la vista nocturna!',
      ],
      clerk: [
        'Benvingut a Book Tree. Pots llegir el que vulguis, sense pressa.',
        'La secció d’anglès és a la segona planta, al costat de les escales.',
        'Pràctica d’anglès: “Could you recommend a good book?”', // English practice
      ],
      trashLady: [
        'Corre, corre! Quan sents «La pregària d’una verge», és que ve el camió!',
        'Les escombraries, al camió groc; el reciclatge, al blanc.',
        'Pràctica d’anglès: “Don’t forget to take out the trash!”', // English practice
      ],
      vendor: [
        'Tot el que hi ha aquí ho he fet jo a mà!',
        'Pràctica d’anglès: “How much is this?” “It is twenty dollars.”', // English practice
      ],
      taichi: [
        'A poc a poc… inspira… expira. El taitxí em manté jove!',
        'Tinc vuitanta-dos anys. Vinc aquí cada matí.',
        'Pràctica d’anglès: “Breathe in. Breathe out. Relax.”', // English practice
      ],
      taxiDriver: [
        'On anem? Em conec tots els carrers de Taipei!',
        'Pràctica d’anglès: “Please fasten your seat belt.”', // English practice
      ],
      performer: [
        'Un globus per a tu! El vols de gos o de girafa?',
        'Pràctica d’anglès: “That was amazing!”: això és el que diu el públic.', // English practice
      ],
      teaSeller: 'Benvingut a Bubble Bear! Quant de sucre? Quant de gel?',
    },
  },
};
